import { put, call, getContext, select } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { merge as _merge } from 'lodash';
import { buildUrl, createErrorRemoveReducer, createErrorClearReducer } from './utils';
import { template } from '../utils/misc';

export function createDeleteSaga(url, entityDescriptor, defaultOptions) {
  const { logger } = entityDescriptor;
  const urlTemplate = template(url);
  const defaultQueryParams = defaultOptions.defaultQueryParams || {};

  const getLocale = createSelector(
    state => state.intl,
    state => state.locale
  );

  return function* deleteSaga({ payload: { entity, options = {} } }) {
    try {
      const http = yield getContext('http');
      const locale = yield select(getLocale);
      const urlParameter = Object.assign({ entity, options: options || {} });
      const requestUrl = buildUrl(urlTemplate(urlParameter), _merge(defaultQueryParams, options.queryParams), locale);
      const requestConfig = defaultOptions.requestConfig
        ? { ...defaultOptions.requestConfig, ...options.requestConfig }
        : options.requestConfig;

      logger.debug('Delete entity URL', url, 'defaultOptions', defaultOptions, 'Options', options);
      logger.debug('Delete entity RequestUrl', requestUrl, 'RequestConfig', requestConfig);

      const response = yield call(http.delete, requestUrl, requestConfig);
      if (response.status < 200 || response.status > 299) {
        logger.error('Response with HTTP status', response.status, response);
        yield put(entityDescriptor.deleteFailure(response.data));
      } else {
        yield put(entityDescriptor.deleteComplete({ entity, options }));
      }
    } catch (e) {
      logger.error('Exception occurred while deleting entity', e);
      yield put(entityDescriptor.fetchDataFailure(e));
    }
  };
}

export function createDeleteActionTypes(actionPrefix) {
  return {
    DELETE: actionPrefix + '_DELETE',
    DELETE_LOADING: actionPrefix + '_DELETE_LOADING',
    DELETE_COMPLETE: actionPrefix + '_DELETE_COMPLETE',
    DELETE_FAILURE: actionPrefix + '_DELETE_FAILURE',
    DELETE_REMOVE_ERROR: actionPrefix + '_DELETE_REMOVE_ERROR',
    DELETE_CLEAR_ERRORS: actionPrefix + '_DELETE_CLEAR_ERRORS',
  };
}

export function createDeleteActions(actionTypes) {
  return {
    delete: (entity, options = {}) => ({ type: actionTypes.DELETE, payload: { entity, options } }),
    deleteComplete: ({ entity, options }) => ({
      type: actionTypes.DELETE_COMPLETE,
      payload: { entity, options },
    }),
    deleteFailure: error => ({
      type: actionTypes.DELETE_FAILURE,
      payload: { error },
      error: true,
    }),
    deleteRemoveError: index => ({ type: actionTypes.DELETE_REMOVE_ERROR, payload: { index } }),
    deleteClearErrors: () => ({ type: actionTypes.DELETE_CLEAR_ERRORS }),
  };
}

export function createDeleteReduceFunctions(moduleDescriptor, options) {
  return {
    [moduleDescriptor.actionTypes.DELETE]: state => {
      return { ...state, deleteLoading: true };
    },
    [moduleDescriptor.actionTypes.DELETE_COMPLETE]: (state, { entity }) => ({
      ...state,
      count: state.count - 1,
      list: state.list.filter(item => item[options.primaryKey] !== entity[options.primaryKey]),
      data: {},
      deleteLoading: false,
    }),
    [moduleDescriptor.actionTypes.DELETE_FAILURE]: (state, { error }) => ({
      ...state,
      deleteErrors: [...state.deleteErrors, error],
      deleteLoading: false,
    }),
    [moduleDescriptor.actionTypes.DELETE_REMOVE_ERROR]: createErrorRemoveReducer('deleteErrors'),
    [moduleDescriptor.actionTypes.DELETE_CLEAR_ERRORS]: createErrorClearReducer('deleteErrors'),
  };
}

export function createDeleteSelectors(moduleName, entity) {
  const selectEntity = state => state[moduleName][entity.name];

  return {
    isDeleting: createSelector(selectEntity, entityState => entityState.deleteLoading),
    getDeleteErrors: createSelector(selectEntity, entityState => entityState.deleteErrors),
    hasDeleteErrors: createSelector(selectEntity, entityState => entityState.deleteErrors.length > 0),
  };
}
